import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Home as HomeIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  Tune as TuneIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useSearch, PropertySearchParams, SearchFilters } from '@/contexts/SearchContext';
import { useInfinitePropertySearch } from '@/hooks/api/usePropertySearch';
import FilterBar from '@/components/Search/FilterBar';
import PropertyGrid from '@/components/Search/PropertyGrid';
import SearchBar from '@/components/Search/SearchBar';

const ListingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const {
    state,
    setSearchParams: setContextSearchParams,
    setActiveFilters,
    addRecentSearch,
    hasActiveFilters,
    getActiveFilterCount,
  } = useSearch();

  // Sort menu state
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const sortMenuOpen = Boolean(sortAnchorEl);

  // Parse URL parameters into search params
  const urlSearchParams = useMemo((): PropertySearchParams => {
    const params: PropertySearchParams = {};
    
    if (searchParams.get('location')) params.location = searchParams.get('location')!;
    if (searchParams.get('checkIn')) params.checkIn = searchParams.get('checkIn')!;
    if (searchParams.get('checkOut')) params.checkOut = searchParams.get('checkOut')!;
    if (searchParams.get('guests')) params.guests = parseInt(searchParams.get('guests')!);
    if (searchParams.get('priceMin')) params.priceMin = parseInt(searchParams.get('priceMin')!);
    if (searchParams.get('priceMax')) params.priceMax = parseInt(searchParams.get('priceMax')!);
    if (searchParams.get('sortBy')) params.sortBy = searchParams.get('sortBy') as any;
    
    // Handle array parameters
    const propertyTypes = searchParams.getAll('propertyTypes');
    if (propertyTypes.length > 0) params.propertyTypes = propertyTypes;
    
    const amenities = searchParams.getAll('amenities');
    if (amenities.length > 0) params.amenities = amenities;
    
    if (searchParams.get('instantBook') === 'true') params.instantBook = true;
    
    return params;
  }, [searchParams]);

  // Merge URL params with active filters for the search query
  const searchQuery = useMemo((): PropertySearchParams => {
    return {
      ...urlSearchParams,
      priceMin: state.activeFilters.priceRange.min > 0 ? state.activeFilters.priceRange.min : urlSearchParams.priceMin,
      priceMax: state.activeFilters.priceRange.max < 1000 ? state.activeFilters.priceRange.max : urlSearchParams.priceMax,
      propertyTypes: state.activeFilters.propertyTypes.length > 0 ? state.activeFilters.propertyTypes : urlSearchParams.propertyTypes,
      amenities: state.activeFilters.amenities.length > 0 ? state.activeFilters.amenities : urlSearchParams.amenities,
      instantBook: state.activeFilters.instantBook !== undefined ? state.activeFilters.instantBook : urlSearchParams.instantBook,
      limit: 20, // Default page size
    };
  }, [urlSearchParams, state.activeFilters]);

  // Use infinite query for search results
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useInfinitePropertySearch(searchQuery, true);

  // Flatten all pages into a single array
  const allListings = useMemo(() => {
    return data?.pages.flatMap(page => page.listings) || [];
  }, [data]);

  // Get total count from first page
  const totalCount = data?.pages[0]?.totalCount || 0;

  // Update context when URL params change
  useEffect(() => {
    setContextSearchParams(urlSearchParams);
    
    // Add to recent searches if there are meaningful search criteria
    if (urlSearchParams.location || urlSearchParams.checkIn || urlSearchParams.checkOut || urlSearchParams.guests) {
      addRecentSearch(urlSearchParams);
    }
  }, [urlSearchParams, setContextSearchParams, addRecentSearch]);

  // Update URL when filters change
  const updateURL = useCallback((newParams: PropertySearchParams, newFilters?: SearchFilters) => {
    const params = new URLSearchParams();
    
    // Add search parameters to URL
    if (newParams.location) params.set('location', newParams.location);
    if (newParams.checkIn) params.set('checkIn', newParams.checkIn);
    if (newParams.checkOut) params.set('checkOut', newParams.checkOut);
    if (newParams.guests) params.set('guests', newParams.guests.toString());
    if (newParams.sortBy) params.set('sortBy', newParams.sortBy);
    
    // Add filter parameters to URL
    if (newFilters) {
      if (newFilters.priceRange.min > 0) params.set('priceMin', newFilters.priceRange.min.toString());
      if (newFilters.priceRange.max < 1000) params.set('priceMax', newFilters.priceRange.max.toString());
      
      newFilters.propertyTypes.forEach(type => params.append('propertyTypes', type));
      newFilters.amenities.forEach(amenity => params.append('amenities', amenity));
      
      if (newFilters.instantBook) params.set('instantBook', 'true');
    }
    
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Handle new search from search bar
  const handleSearch = useCallback((newSearchParams: PropertySearchParams) => {
    // Clear existing results when starting a new search
    updateURL(newSearchParams, state.activeFilters);
    
    // Scroll to top when new search is performed
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [updateURL, state.activeFilters]);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: SearchFilters) => {
    setActiveFilters(newFilters);
    updateURL(urlSearchParams, newFilters);
    
    // Scroll to top when filters change to show new results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setActiveFilters, updateURL, urlSearchParams]);

  // Handle listing click
  const handleListingClick = useCallback((listing: any) => {
    navigate(`/listing/${listing.listingId}`);
  }, [navigate]);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle retry
  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Handle sort menu
  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (sortBy: PropertySearchParams['sortBy']) => {
    const newParams = { ...urlSearchParams, sortBy };
    updateURL(newParams, state.activeFilters);
    handleSortClose();
  };

  // Get sort options
  const sortOptions = [
    { value: undefined, label: 'Recommended', icon: <StarIcon /> },
    { value: 'newest' as const, label: 'Newest first', icon: <ScheduleIcon /> },
    { value: 'price_asc' as const, label: 'Price: Low to High', icon: <ArrowUpIcon /> },
    { value: 'price_desc' as const, label: 'Price: High to Low', icon: <ArrowDownIcon /> },
  ];

  const getCurrentSortLabel = () => {
    const current = sortOptions.find(option => option.value === urlSearchParams.sortBy);
    return current?.label || 'Recommended';
  };

  // Get search summary text
  const getSearchSummary = () => {
    const parts = [];
    
    if (urlSearchParams.location) {
      parts.push(`in ${urlSearchParams.location}`);
    }
    
    if (urlSearchParams.checkIn && urlSearchParams.checkOut) {
      const checkIn = new Date(urlSearchParams.checkIn).toLocaleDateString();
      const checkOut = new Date(urlSearchParams.checkOut).toLocaleDateString();
      parts.push(`${checkIn} - ${checkOut}`);
    }
    
    if (urlSearchParams.guests) {
      parts.push(`${urlSearchParams.guests} ${urlSearchParams.guests === 1 ? 'guest' : 'guests'}`);
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'All properties';
  };

  // Get active filters summary
  const getActiveFiltersText = () => {
    const count = getActiveFilterCount();
    if (count === 0) return null;
    
    return `${count} ${count === 1 ? 'filter' : 'filters'} applied`;
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              textDecoration: 'none',
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <HomeIcon fontSize="small" />
            Home
          </Link>
          <Typography variant="body2" color="text.primary">
            Search Results
          </Typography>
        </Breadcrumbs>

        {/* Search Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component="h1"
            fontWeight={600}
            sx={{ mb: 1 }}
          >
            {totalCount > 0 ? `${totalCount.toLocaleString()} properties` : 'Search properties'}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {getSearchSummary()}
          </Typography>

          {/* Search Bar for mobile */}
          {isMobile && (
            <Box sx={{ mb: 3 }}>
              <SearchBar
                onSearch={handleSearch}
                placeholder="Update your search..."
                size="medium"
                variant="compact"
              />
            </Box>
          )}

          {/* Search Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {/* Active filters summary */}
            {getActiveFiltersText() && (
              <Chip
                icon={<TuneIcon />}
                label={getActiveFiltersText()}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.main',
                  '& .MuiChip-icon': {
                    color: 'primary.contrastText',
                  },
                }}
              />
            )}
            
            {/* Sort indicator */}
            {urlSearchParams.sortBy && (
              <Chip
                icon={<SortIcon />}
                label={`Sorted by ${getCurrentSortLabel()}`}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'grey.100',
                }}
              />
            )}
            
            {/* Results count for mobile */}
            {isMobile && totalCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {totalCount.toLocaleString()} {totalCount === 1 ? 'property' : 'properties'} found
              </Typography>
            )}
          </Box>
        </Box>

        {/* Filter Bar and Sort */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <FilterBar
              onFiltersChange={handleFiltersChange}
              showResultsCount={true}
              resultsCount={totalCount}
            />
          </Box>
          
          {/* Sort Button */}
          {!isMobile && totalCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              onClick={handleSortClick}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 'fit-content',
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: 'divider',
                },
              }}
            >
              {getCurrentSortLabel()}
            </Button>
          )}
        </Box>

        {/* Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={sortMenuOpen}
          onClose={handleSortClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 1,
              minWidth: 200,
            },
          }}
        >
          {sortOptions.map((option) => (
            <MenuItem
              key={option.value || 'default'}
              onClick={() => handleSortChange(option.value)}
              selected={urlSearchParams.sortBy === option.value}
              sx={{
                py: 1.5,
                px: 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {option.icon}
              </ListItemIcon>
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{
                  fontWeight: urlSearchParams.sortBy === option.value ? 600 : 400,
                }}
              />
            </MenuItem>
          ))}
        </Menu>

        {/* Error State */}
        {isError && !isLoading && allListings.length === 0 && (
          <Box sx={{ py: 4 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleRetry}>
                  Try Again
                </Button>
              }
            >
              {error instanceof Error ? error.message : 'Failed to load search results. Please check your connection and try again.'}
            </Alert>
          </Box>
        )}

        {/* Loading State for Filter Changes */}
        {isLoading && allListings.length > 0 && (
          <Box
            sx={{
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1,
                borderRadius: 2,
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                backgroundColor: 'background.paper',
                px: 3,
                py: 2,
                borderRadius: 2,
                boxShadow: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Updating results...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Results Grid */}
        <Box>
          <PropertyGrid
            listings={allListings}
            loading={isLoading}
            hasMore={hasNextPage}
            totalCount={totalCount}
            onLoadMore={handleLoadMore}
            onListingClick={handleListingClick}
            onRetry={handleRetry}
            error={isError && !isLoading ? (error instanceof Error ? error.message : 'Failed to load properties') : null}
            emptyStateTitle="No properties found"
            emptyStateDescription={
              hasActiveFilters() 
                ? "No properties match your current filters. Try adjusting your search criteria."
                : "Try adjusting your search criteria or browse all available properties."
            }
            emptyStateAction={
              hasActiveFilters() ? (
                <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                  <Button
                    variant="outlined"
                    startIcon={<TuneIcon />}
                    onClick={() => {
                      // Clear all filters
                      const clearedFilters: SearchFilters = {
                        priceRange: { min: 0, max: 1000 },
                        propertyTypes: [],
                        amenities: [],
                        instantBook: undefined,
                      };
                      handleFiltersChange(clearedFilters);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear filters
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={() => navigate('/')}
                    sx={{ textTransform: 'none' }}
                  >
                    Start a new search
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => navigate('/')}
                  sx={{ textTransform: 'none' }}
                >
                  Start a new search
                </Button>
              )
            }
            infiniteScroll={true}
            variant="default"
          />
        </Box>

        {/* Loading more indicator */}
        {isFetchingNextPage && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading more properties...
            </Typography>
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default ListingsPage;